/**
 * Winhouse — Reset Password page. Accepts a one-time token from the URL,
 * lets the user choose a new password (8 char minimum), and redirects to
 * the unified portal login on success.
 */
import React, { useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Lock, CheckCircle2, AlertCircle, ArrowLeft, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const { token: pathToken } = useParams();
  const [qs] = useSearchParams();
  const token = pathToken || qs.get('token') || '';
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const valid = pw.length >= 8 && pw === confirm;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!token) { setError('Missing reset token in the URL.'); return; }
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pw !== confirm) { setError('Passwords do not match.'); return; }
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: pw }, { withCredentials: true });
      setDone(true);
      toast.success('Password reset successful — you can now sign in.');
      setTimeout(() => navigate('/portal/login', { replace: true }), 2200);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not reset password.';
      setError(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Winhouse
        </Link>

        <div className="bg-white border border-gray-200 rounded-2xl p-7 shadow-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-red-700 font-semibold mb-2">
            <Sparkles className="h-3.5 w-3.5" /> Winhouse Portal
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Choose a new password</h1>
          <p className="text-sm text-gray-600 mt-2">
            Make it at least 8 characters. You'll be signed out of any open sessions.
          </p>

          {done ? (
            <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-sm" data-testid="reset-success">
              <div className="flex items-center gap-2 text-emerald-800 font-bold mb-1">
                <CheckCircle2 className="h-4 w-4" /> Password updated
              </div>
              <p className="text-emerald-900/80 text-[13px]">Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-[12px] text-red-800 flex items-start gap-2" data-testid="reset-error">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">New password</label>
                <input type={show ? 'text' : 'password'} required autoFocus value={pw} onChange={(e) => setPw(e.target.value)}
                  placeholder="At least 8 characters" autoComplete="new-password" minLength={8}
                  className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200"
                  data-testid="reset-new-password" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1.5">Confirm new password</label>
                <input type={show ? 'text' : 'password'} required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Type it again" autoComplete="new-password" minLength={8}
                  className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200"
                  data-testid="reset-confirm-password" />
              </div>
              <label className="flex items-center gap-2 text-[12px] text-gray-600 cursor-pointer">
                <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} data-testid="reset-show-password" /> Show password
              </label>
              <button type="submit" disabled={submitting || !valid}
                className="w-full h-11 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="reset-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Reset password
              </button>
              <div className="text-center text-[12px] text-gray-500 pt-2 border-t border-gray-100">
                Need a new link? <Link to="/forgot-password" className="text-red-700 font-semibold hover:underline">Request again</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
